import { createTheme, responsiveFontSizes } from "@material-ui/core/styles";
import {
  green,
  red,
  grey,
  yellow,
  orange,
  purple,
} from "@material-ui/core/colors";

const fontStack = [
  "Switzer",
  "-apple-system",
  "BlinkMacSystemFont",
  '"Segoe UI"',
  "Roboto",
  "Oxygen",
  "Ubuntu",
  "Cantarell",
  '"Helvetica Neue"',
  "Arial",
  "sans-serif",
].join(",");

export const customerPalette = {
  buttonGradientDefault:
    "linear-gradient(93.21deg, #558ccd 1.22%, #2a6fc0 88.2%)",
  buttonGradientHover:
    "linear-gradient(268.62deg, #2a6fc0 1.2%, #5899D6 99.36%), #C4C4C4",
  buttonGradientActive:
    "linear-gradient(0deg, #2a6fc0, #2a6fc0), linear-gradient(0deg, #2A6FC0, #2A6FC0), #C4C4C4",
  buttonDisabled: "#E0E4EB",
  buttonShadowDefault: "1px 1px 5px rgba(0, 0, 0, 0.1);",
  buttonShadowHover: "1px 1px 5px rgba(0, 0, 0, 0.1);",
  checkboxGradientDefault:
    "linear-gradient(86.12deg, #2A6FC0 12.48%, #558ccd 73.95%), #CBD1DB",
  primaryDark: "#2A6FC0",
  primaryMedium: "#5899D6",
  primaryLight: "#7fa9d9",
  primaryExtraLight: "#aac5e6",
  // MD3 color roles
  primary: "#2A6FC0",
  onPrimary: "#FFFFFF",
  primaryContainer: "#D4E4F6",
  onPrimaryContainer: "#0A1F36",
};

export const talentPalette = {
  buttonGradientDefault:
    "linear-gradient(268.66deg, #358FAB -7.68%, #75BACF 99.38%), #C4C4C4",
  buttonGradientHover:
    "linear-gradient(268.66deg, #358FAB -7.68%, #358FAB 99.38%), #C4C4C4",
  buttonGradientActive: "linear-gradient(0deg, #358FAB, #358FAB), #C4C4C4",
  buttonDisabled: "#E0E4EB",
  buttonShadowDefault: "1px 1px 5px rgba(0, 0, 0, 0.1);",
  buttonShadowHover: "1px 1px 5px rgba(0, 0, 0, 0.1);",
  checkboxGradientDefault:
    "linear-gradient(268.66deg, #358FAB -7.68%, #75BACF 99.38%), #CBD1DB",
  primaryDark: "#277089",
  primaryMedium: "#75BACF",
  primaryLight: "#A8DCEC",
  primaryExtraLight: "#D5ECF3",
  // MD3 color roles
  secondary: "#277089",
  onSecondary: "#FFFFFF",
  secondaryContainer: "#D5ECF3",
  onSecondaryContainer: "#0C2A33",
};

export const basePalette = {
  greyDefault: "#2b2b2b",
  greyMidDark: "#4b4b4b",
  greyLighter: "#5b5b5b",
  greyLight: "#5b5b5b",
  greyMid: "#5b5b5b",
  backgroundDefault: "#FAFBFE",
  buttonDisabled: "#E6E6E6",
  disabledText: "#AEAEAE",
  redMain: "#FF3333",
  redDark: "#AD0404",
  // MD3 surface scale (grey ramp)
  surface: "#FAFBFE",
  onSurface: "#2b2b2b",
  onSurfaceVariant: "#5b5b5b",
  surfaceContainerLowest: "#FFFFFF",
  surfaceContainerLow: "#F5F7FB",
  surfaceContainer: "#EFF2F8",
  surfaceContainerHigh: "#EAEDF4",
  surfaceContainerHighest: "#E4E7F0",
  outline: "#74777F",
  outlineVariant: "#D8DBE4",
  // MD3 error
  error: "#BA1A1A",
  onError: "#FFFFFF",
  errorContainer: "#FFDAD6",
  onErrorContainer: "#410002",
  // MD3 inverse
  inverseSurface: "#2b2b2b",
  inverseOnSurface: "#F0F4FA",
  // Status semantic colors (MD3 foreground + container pattern)
  statusUnavailable: "#5d6c81",
  statusUnavailableContainer: "#e8ecf0",
  statusVaulted: "#6b5b8d",
  statusVaultedContainer: "#ece7f3",
  statusPending: "#b45309",
  statusPendingContainer: "#fef3c7",
  statusCritical: "#BA1A1A",
  statusCriticalContainer: "#FFDAD6",
  statusSuccess: "#128200",
  statusSuccessContainer: "#d4ffcd",
  // Shadow (space-separated RGB channels for dark-mode readiness)
  shadowColor: "0 0 0",
};

const insetFocusRing = (color) => ({
  outline: `2px solid ${color}`,
  outlineOffset: -2,
});

// MUI's shadow index is roughly elevation in dp; MD3 levels 1–5 are 1, 3, 6, 8 and 12dp.
const md3Level1 =
  "0 1px 3px rgba(0, 0, 0, 0.03), 0 4px 14px rgba(0, 0, 0, 0.04)";
const md3Level2 =
  "0 2px 6px rgba(0, 0, 0, 0.035), 0 8px 24px rgba(0, 0, 0, 0.05)";
const md3Level3 =
  "0 4px 12px rgba(0, 0, 0, 0.04), 0 16px 40px rgba(0, 0, 0, 0.06)";
const md3Level4 =
  "0 6px 18px rgba(0, 0, 0, 0.045), 0 24px 56px rgba(0, 0, 0, 0.07)";
const md3Level5 =
  "0 8px 24px rgba(0, 0, 0, 0.05), 0 32px 72px rgba(0, 0, 0, 0.08)";
const md3Shadows = [
  "none",
  ...Array(2).fill(md3Level1),
  ...Array(3).fill(md3Level2),
  ...Array(2).fill(md3Level3),
  ...Array(4).fill(md3Level4),
  ...Array(13).fill(md3Level5),
];

const theme = responsiveFontSizes(
  createTheme({
    shadows: md3Shadows,
    zIndex: {
      mobileStepper: 200,
      speedDial: 200,
      appBar: 200,
      drawer: 300,
      modal: 400,
      snackbar: 600,
      tooltip: 700,
    },
    palette: {
      primary: {
        main: customerPalette.primaryDark,
        medium: customerPalette.primaryMedium,
        light: customerPalette.primaryLight,
        extraLight: customerPalette.primaryExtraLight,
        background: basePalette.backgroundDefault,
      },
      secondary: {
        main: talentPalette.primaryDark,
        medium: talentPalette.primaryMedium,
        light: talentPalette.primaryLight,
        extraLight: talentPalette.primaryExtraLight,
        background: basePalette.backgroundDefault,
      },
      green: {
        limeLight: "#D4FFCD",
        limeDark: "#18AA00",
        main: green[600],
        light: green[400],
        extraLight: green[100],
        dark: green[800],
        extraDark: green[900],
      },
      red: {
        main: basePalette.redMain,
        light: red[400],
        dark: basePalette.redDark,
      },
      yellow: {
        main: yellow[600],
        light: yellow[400],
        dark: yellow[800],
      },
      grey: {
        main: basePalette.greyDefault,
        dark: grey[900],
        midDark: basePalette.greyMidDark,
        light: basePalette.greyLight,
        lighter: basePalette.greyLighter,
        mid: basePalette.greyMid,
        disabled: basePalette.buttonDisabled,
      },
      blue: {
        main: "#2A6FC0",
        light: "#558ccd",
      },
      light: {
        main: "#F9F9F9",
      },
      background: {
        default: basePalette.surface,
        paper: basePalette.surfaceContainerLowest,
        main: basePalette.surface,
        light: basePalette.surfaceContainerLowest,
        surfaceContainerLowest: basePalette.surfaceContainerLowest,
        surfaceContainerLow: basePalette.surfaceContainerLow,
        surfaceContainer: basePalette.surfaceContainer,
        surfaceContainerHigh: basePalette.surfaceContainerHigh,
        surfaceContainerHighest: basePalette.surfaceContainerHighest,
      },
      orange: {
        ...orange,
      },
      purple: {
        ...purple,
      },
      text: {
        primary: basePalette.greyDefault,
        secondary: basePalette.greyLight,
      },
      status: {
        statusUnavailable: basePalette.statusUnavailable,
        statusUnavailableContainer: basePalette.statusUnavailableContainer,
        statusVaulted: basePalette.statusVaulted,
        statusVaultedContainer: basePalette.statusVaultedContainer,
        statusPending: basePalette.statusPending,
        statusPendingContainer: basePalette.statusPendingContainer,
        statusCritical: basePalette.statusCritical,
        statusCriticalContainer: basePalette.statusCriticalContainer,
        statusSuccess: basePalette.statusSuccess,
        statusSuccessContainer: basePalette.statusSuccessContainer,
      },
    },
    shape: {
      borderRadius: 8,
      // MD3 shape scale
      xs: 4,
      sm: 8,
      md: 12,
      lg: 16,
      xl: 28,
      full: 9999,
    },
    gradient: {
      primary: customerPalette.buttonGradientDefault,
      primaryDark: customerPalette.buttonGradientHover,
      secondary: talentPalette.buttonGradientDefault,
      secondaryDark: talentPalette.buttonGradientHover,
      checkboxGradientCustomer: customerPalette.checkboxGradientDefault,
      checkboxGradientTalent: talentPalette.checkboxGradientDefault,
      grey: "linear-gradient(283.7deg, #262C38 10.95%, #525A6B 89.36%)",
      pink: "linear-gradient(104.14deg, #C0E5FF 9.51%, rgba(214, 245, 255, 0.6) 50.36%, #EDD1EE 92.53%);",
      button: customerPalette.buttonGradientDefault,
      buttonHover: customerPalette.buttonGradientHover,
      text: `linear-gradient(93.21deg, #358FAB 1.22%, #2a6fc0 88.2%), #2b2b2b`,
      textReverse: `linear-gradient(93.21deg, #2a6fc0 1.22%, #358FAB 88.2%), #2b2b2b`,
      gold: "linear-gradient(90deg, #FFE68E 0%, #C2A049 100%), linear-gradient(90deg, #FFC48E 0%, #B491FF 100%), #FFFFFF;",
      blue: "linear-gradient(86.12deg, #2A6FC0 12.48%, #558ccd 73.95%), #CBD1DB",
      lightBlue: "linear-gradient(0deg, #00AFE6, #00AFE6), #FFFFFF;",
      lightBlueHover: "linear-gradient(0deg, #0095C4, #0095C4), #FFFFFF;",
    },
    boxShadow: {
      buttonDefault: customerPalette.buttonShadowDefault,
      buttonHover: customerPalette.buttonShadowHover,
      softCard: "0px 2px 10px rgba(0, 0, 0, 0.1);",
      soft: "0 1px 12px 4px rgba(0, 0, 50, 0.05)",
      light: "0px 0px 10.2765px rgba(0, 0, 0, 0.1)",
      medium: "0px 0px 18px rgba(0, 0, 0, 0.1)",
      mediumLight: "0px 0px 16px rgba(0, 0, 0, 0.1)",
      mediumDark: "2px 2px 4px rgba(0, 0, 0, 0.25)",
      faint: "2px 2px 10px 0px #0000001A",
      sharp: "0 2px 8px rgba(0, 0, 0, 0.25)",
      offsetDark: "4px 4px 15px rgba(0, 0, 0, 0.25)",
      tight: "1.25px 1.25px 6.25px rgba(0, 0, 0, 0.1)",
      big: "0px 27px 141px rgba(0, 0, 0, 0.07), 0px 9.85544px 51.4673px rgba(0, 0, 0, 0.0374541), 0px 4.78464px 24.9864px rgba(0, 0, 0, 0.0288311), 0px 2.34552px 12.2488px rgba(0, 0, 0, 0.0236566);",
      // MD3 elevation
      elevation1:
        "0 1px 3px rgba(0, 0, 0, 0.03), 0 4px 14px rgba(0, 0, 0, 0.04)",
      elevation2:
        "0 2px 6px rgba(0, 0, 0, 0.035), 0 8px 24px rgba(0, 0, 0, 0.05)",
      elevation3:
        "0 4px 12px rgba(0, 0, 0, 0.04), 0 16px 40px rgba(0, 0, 0, 0.06)",
    },
    typography: {
      fontFamily: fontStack,
      body1: {
        fontFamily: fontStack,
        color: basePalette.greyDefault,
      },
      body2: {
        fontFamily: fontStack,
        color: basePalette.greyDefault,
      },
      h1: {
        fontFamily: fontStack,
        fontWeight: 700,
        color: basePalette.greyDefault,
      },
      h2: {
        fontFamily: fontStack,
        fontWeight: 600,
        color: basePalette.greyDefault,
      },
      h3: {
        fontFamily: fontStack,
        fontWeight: 600,
        color: basePalette.greyDefault,
      },
      h4: {
        fontFamily: fontStack,
        fontWeight: 700,
        color: basePalette.greyDefault,
      },
      h5: {
        fontFamily: fontStack,
        fontWeight: 700,
        color: basePalette.greyDefault,
      },
      h6: {
        fontFamily: fontStack,
        fontWeight: 600,
        color: basePalette.greyDefault,
      },
      button: {
        fontFamily: fontStack,
        fontWeight: 700,
        textTransform: "none",
      },
      caption: {
        fontFamily: fontStack,
        color: basePalette.greyDefault,
      },
      subtitle1: {
        fontFamily: fontStack,
        fontWeight: 500,
        color: basePalette.greyLight,
        lineHeight: "1.5rem",
      },
      subtitle2: {
        fontFamily: fontStack,
        fontWeight: 500,
        color: basePalette.greyDefault,
        lineHeight: "1.5rem",
      },
      smallBoldText: {
        fontFamily: fontStack,
        fontWeight: 600,
        fontSize: "0.8rem",
      },
      label: {
        fontFamily: fontStack,
      },
      light: 300,
      normal: 400,
      medium: 500,
      semiBold: 600,
      bold: 700,
      fontStack,
      // deprecated — use fontStack instead
      interFontStack: fontStack,
    },
    props: {
      MuiButtonBase: {
        disableRipple: true,
      },
    },
    overrides: {
      MuiDivider: {
        root: {
          background: basePalette.outlineVariant,
          backgroundColor: basePalette.outlineVariant,
        },
      },
      MuiOutlinedInput: {
        input: {
          background: basePalette.surfaceContainerLowest,
          borderRadius: 8,
        },
        notchedOutline: {
          borderColor: basePalette.outlineVariant,
          borderRadius: 8,
          transition: "border-color 150ms ease",
        },
        root: {
          background: basePalette.surfaceContainerLowest,
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: basePalette.outline,
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: customerPalette.primary,
            borderWidth: 2,
          },
        },
      },
      MuiFormHelperText: {
        root: {
          color: basePalette.greyLight,
        },
      },
      MuiIconButton: {
        root: {
          "&:focus-visible": insetFocusRing(customerPalette.primary),
        },
      },
      MuiCheckbox: {
        root: {
          color: basePalette.outline,
        },
        colorPrimary: {
          "&.Mui-checked": {
            color: customerPalette.primary,
          },
        },
      },
      MuiButton: {
        text: {
          color: basePalette.greyDefault,
          fontWeight: 600,
          padding: "6px 16px",
        },
        textPrimary: {
          color: customerPalette.primaryDark,
        },
        textSecondary: {
          color: talentPalette.primaryDark,
        },
        textSizeSmall: {
          fontSize: "0.75rem",
        },
        outlined: {
          borderColor: basePalette.outlineVariant,
          transition: "border-color 150ms ease, background-color 150ms ease",
          "&:hover": {
            borderColor: basePalette.outline,
          },
          "&:focus-visible": insetFocusRing(customerPalette.primary),
        },
        root: {
          borderRadius: 9999,
        },
        contained: {
          boxShadow: customerPalette.buttonShadowDefault,
          color: basePalette.greyLight,
          borderRadius: 9999,
          padding: "8px 16px",
          fontWeight: 600,
          transition: "box-shadow 150ms ease, background-color 150ms ease",
          "&:focus-visible": insetFocusRing(customerPalette.primary),
          "&:hover": {
            boxShadow: customerPalette.buttonShadowHover,
          },
          "&:active": {
            boxShadow: "none",
          },
          "&:disabled": {
            background: customerPalette.buttonDisabled,
          },
          "&.blue": {
            background: "linear-gradient(0deg, #00AFE6, #00AFE6), #FFFFFF;",
            transition: "all 150ms ease",
            color: "white",
          },
          "&.blue:hover": {
            background: "linear-gradient(0deg, #00A6DB, #00A6DB), #FFFFFF",
          },
        },
        containedPrimary: {
          backgroundColor: customerPalette.primary,
          color: customerPalette.onPrimary,
          borderRadius: 9999,
          padding: "8px 16px",
          boxShadow: "none",
          outline: "initial",
          transition: "background-color 150ms ease, box-shadow 150ms ease",
          "&:hover": {
            backgroundColor: customerPalette.primaryDark,
            boxShadow:
              "0 1px 2px rgba(0, 0, 0, 0.06), 0 2px 6px rgba(0, 0, 0, 0.1)",
          },
          "&:focus-visible": insetFocusRing(customerPalette.onPrimary),
          "&:active": {
            boxShadow: "none",
          },
          "&:disabled": {
            backgroundColor: basePalette.buttonDisabled,
            color: basePalette.disabledText,
            boxShadow: "none",
          },
        },
        containedSecondary: {
          backgroundColor: talentPalette.secondary,
          color: talentPalette.onSecondary,
          borderRadius: 9999,
          padding: "8px 16px",
          boxShadow: "none",
          outline: "initial",
          transition: "background-color 150ms ease, box-shadow 150ms ease",
          "&:hover": {
            backgroundColor: talentPalette.primaryDark,
            boxShadow:
              "0 1px 2px rgba(0, 0, 0, 0.06), 0 2px 6px rgba(0, 0, 0, 0.1)",
          },
          "&:focus-visible": insetFocusRing(talentPalette.onSecondary),
          "&:active": {
            boxShadow: "none",
          },
          "&:disabled": {
            backgroundColor: basePalette.buttonDisabled,
            color: basePalette.disabledText,
            boxShadow: "none",
          },
        },
        containedSizeLarge: {
          padding: "12px 36px",
          minHeight: 48,
        },
        containedSizeSmall: {
          padding: "6px 16px",
          minHeight: 22,
        },
      },
      MuiSwitch: {
        root: {
          width: 36,
          height: 20,
          padding: 0,
          boxSizing: "border-box",
        },
        switchBase: {
          boxSizing: "border-box",
          padding: 3,
          "&$checked": {
            color: "white",
            transform: "translateX(16px)",
            "& + $track": {
              background: basePalette.greyLight,
              backgroundColor: basePalette.greyLight,
              opacity: 1,
              border: "none",
            },
          },
        },
        thumb: {
          width: 14,
          height: 14,
          boxSizing: "border-box",
          border: `1px solid ${basePalette.outlineVariant}`,
          boxShadow: "none",
        },
        track: {
          boxSizing: "border-box",
          borderRadius: 16,
          border: "none",
          backgroundColor: basePalette.buttonDisabled,
          opacity: 1,
          transition: "background-color 0.2s ease-out",
        },
        colorPrimary: {
          "&$checked": {
            color: "white",
            "& + $track": {
              background: customerPalette.checkboxGradientDefault,
            },
          },
          "& + $track": {
            background: basePalette.buttonDisabled,
          },
        },
        colorSecondary: {
          "&$checked": {
            color: "white",
            "& + $track": {
              background: talentPalette.checkboxGradientDefault,
            },
          },
          "& + $track": {
            background: basePalette.buttonDisabled,
          },
        },
      },
      MuiCard: {
        root: {
          borderRadius: 12,
          transition: "box-shadow 150ms ease",
        },
      },
      MuiDialog: {
        paper: {
          borderRadius: 12,
        },
      },
      MuiBackdrop: {
        root: {
          backgroundColor: "rgba(0, 0, 0, 0.32)",
          backdropFilter: "blur(4px)",
        },
      },
      MuiChip: {
        root: {
          borderRadius: 9999,
          fontWeight: 500,
          fontSize: "0.75rem",
          transition: "all 150ms ease",
        },
        outlined: {
          borderColor: basePalette.outline,
          "&:hover": {
            borderColor: basePalette.onSurface,
          },
          "&:focus-visible": insetFocusRing(customerPalette.primary),
        },
        outlinedPrimary: {
          borderColor: customerPalette.primary,
        },
        colorPrimary: {
          backgroundColor: customerPalette.primaryContainer,
          color: customerPalette.onPrimaryContainer,
        },
      },
      MuiMenu: {
        paper: {
          borderRadius: 12,
          border: `1px solid ${basePalette.outlineVariant}`,
        },
      },
      MuiMenuItem: {
        root: {
          transition: "background-color 150ms ease",
          "&:hover": {
            backgroundColor: basePalette.surfaceContainerLow,
          },
          "&.Mui-selected": {
            backgroundColor: basePalette.surfaceContainerLow,
            "&:hover": {
              backgroundColor: basePalette.surfaceContainer,
            },
          },
        },
      },
      MuiPopover: {
        paper: {
          borderRadius: 12,
          border: `1px solid ${basePalette.outlineVariant}`,
        },
      },
      MuiSelect: {
        outlined: {
          borderRadius: 8,
        },
      },
      MuiTooltip: {
        tooltip: {
          backgroundColor: basePalette.inverseSurface,
          color: basePalette.inverseOnSurface,
          borderRadius: 4,
          fontSize: "0.75rem",
          fontWeight: 500,
        },
        arrow: {
          color: basePalette.inverseSurface,
        },
      },
      MuiSnackbarContent: {
        root: {
          backgroundColor: basePalette.inverseSurface,
          color: basePalette.inverseOnSurface,
          borderRadius: 8,
        },
      },
      MuiRadio: {
        root: {
          color: basePalette.outline,
        },
        colorPrimary: {
          "&.Mui-checked": {
            color: customerPalette.primary,
          },
        },
      },
      MuiAlert: {
        root: {
          borderRadius: 8,
          fontWeight: 500,
        },
        standardError: {
          backgroundColor: basePalette.errorContainer,
          color: basePalette.onErrorContainer,
        },
        standardSuccess: {
          backgroundColor: basePalette.statusSuccessContainer,
          color: basePalette.onSurface,
        },
        standardInfo: {
          backgroundColor: customerPalette.primaryContainer,
          color: customerPalette.onPrimaryContainer,
        },
        standardWarning: {
          backgroundColor: basePalette.statusPendingContainer,
          color: basePalette.onSurface,
        },
      },
      MuiInputLabel: {
        root: {
          "&.Mui-focused": {
            color: customerPalette.primary,
          },
        },
      },
      MuiTabs: {
        indicator: {
          backgroundColor: customerPalette.primary,
        },
      },
      MuiTab: {
        root: {
          textTransform: "none",
          fontWeight: 600,
          transition: "color 150ms ease",
          "&:focus-visible": insetFocusRing(customerPalette.primary),
        },
      },
      MuiListItem: {
        root: {
          "&.Mui-selected": {
            backgroundColor: basePalette.surfaceContainerLow,
            "&:hover": {
              backgroundColor: basePalette.surfaceContainer,
            },
          },
        },
        button: {
          transition: "background-color 150ms ease",
          "&:hover": {
            backgroundColor: basePalette.surfaceContainerLow,
          },
        },
      },
    },
  }),
);

export default theme;
